<script lang="ts" setup>
import { buttonVariantClasses, type ButtonDisplay, type ButtonVariant } from '../../ideo/ui-button/classes';

type ActionButtonVariant = ButtonVariant;

const instance = getCurrentInstance();

defineOptions({
    name: 'ActionButton',
    inheritAttrs: false
});

const props = withDefaults(defineProps<{
    icon?: string;
    iconClass?: string | string[] | Record<string, boolean>;
    text?: string;
    to?: Record<string, any> | null;
    title?: string | null;
    tag?: string;
    loading?: boolean;
    confirm?: string;
    confirmAttrs?: any;
    variant?: string;
    nowrap?: boolean;
    back?: boolean;
    submit?: boolean;
    toolbar?: boolean;
    disabled?: boolean;
}>(), {
    icon: null,
    iconClass: () => ([]),
    tag: 'button',
    loading: false,
    confirm: '',
    confirmAttrs: {},
    variant: 'primary',
    nowrap: true,
    title: null,
    text: null,
    to: null
});

const emit = defineEmits<{
    (e: 'click', ev: any): void;
}>();

const attrs = useAttrs();
const loaded = ref(false);
const floating = ref(false);
const hideDropdown = inject<(() => void) | null>('hideDropdown', null);
const actionBarFormId = inject<Ref<string> | null>('actionBarFormId', null);
const dropdown = ref(hideDropdown !== null);

const el = ref<any>(null);

const media = useMedia();
const condition = computed(() =>
{
    if ('condition' in instance.parent)
        return (instance.parent as any).condition;
    else
        return media.mobile();
});

const dropdownButton = computed(() => dropdown.value);
const floatingButton = computed(() => condition.value && floating.value);
const backButton = computed(() => condition.value && props.back && !dropdownButton.value && !floatingButton.value);

const buttonType = computed(() => props.submit ? 'submit' : 'button');

const utilityClasses: Record<string, string> = {
    'ms-auto': 'ms-auto',
    'ms-0': 'ms-0',
    'ms-1': 'ms-1',
    'ms-2': 'ms-2',
    'ms-3': 'ms-3',
    'ms-4': 'ms-4',
    'ms-5': 'ms-5',
    'me-auto': 'me-auto',
    'me-0': 'me-0',
    'me-1': 'me-1',
    'me-2': 'me-2',
    'me-3': 'me-3',
    'me-4': 'me-4',
    'me-5': 'me-5'
};

function translateClassToken(token: string): string
{
    return utilityClasses[token] ?? token;
}

function translateClasses(value: any): any
{
    if (typeof value === 'string')
        return value.split(' ').filter(part => part.length > 0).map(part => translateClassToken(part));

    if (Array.isArray(value))
        return value.map(item => translateClasses(item));

    if (value && typeof value === 'object')
    {
        return Object.entries(value).reduce((result, [key, enabled]) =>
        {
            result[translateClassToken(key)] = enabled;

            return result;
        }, {} as Record<string, any>);
    }

    return value;
}

const variantParts = computed(() => (props.variant ?? 'secondary').split(' ').filter(part => part.length > 0));
const resolvedVariant = computed<ActionButtonVariant>(() =>
{
    const variant = variantParts.value.find(part => part in buttonVariantClasses);

    return (variant ?? 'secondary') as ActionButtonVariant;
});
const variantUtilityClasses = computed(() => variantParts.value
    .filter(part => !(part in buttonVariantClasses))
    .map(part => utilityClasses[part] ?? part));
const attrsClasses = computed(() => translateClasses(attrs.class));
const rootAttrs = computed(() =>
{
    const result = { ...attrs };

    delete result.class;

    return result;
});

const buttonDisplay = computed<ButtonDisplay>(() =>
{
    if (dropdownButton.value)
        return 'dropdown-inline';

    if (floatingButton.value)
        return 'floating';

    if (backButton.value)
        return 'back';

    return 'default';
});

const buttonElement = computed(() => el.value?.$el ?? el.value);

const buttonAttrs = computed(() =>
{
    return {
        ...rootAttrs.value,
        class: [
            'action-button',
            variantUtilityClasses.value,
            attrsClasses.value,
            {
                'gap-2': !dropdownButton.value,
                'whitespace-nowrap': props.nowrap
            }
        ]
    };
});

const textVisible = computed(() =>
{
    if (dropdownButton.value)
        return true;

    if (!props.text)
        return false;

    if (floatingButton.value)
        return false;

    if (backButton.value)
        return false;

    return true;
});

const iconName = computed(() =>
{
    if (backButton.value)
        return props.icon || 'arrow-left';

    return props.icon;
});

const iconClasses = computed(() =>
{
    const classes: Array<string | string[] | Record<string, boolean>> = [
        'action-button-icon shrink-0',
        props.iconClass
    ];

    if (floatingButton.value)
        classes.push('block size-6');

    if (backButton.value)
        classes.push('action-button-icon-back');

    return classes;
});

const buttonText = computed(() =>
{
    if (dropdownButton.value)
        return props.text || props.title;

    return props.text;
});

const iconOnly = computed((): boolean =>
{
    return !dropdownButton.value && !floatingButton.value && !backButton.value && Boolean(iconName.value) && !buttonText.value;
});

onMounted((): void =>
{
    const element = buttonElement.value;
    const parent = element?.closest('.ideo-dropdown-panel');

    dropdown.value = !!parent;
    floating.value = !!element?.closest('.floating');
    loaded.value = true;
});

const triggerClick = (e: any): void =>
{
    emit('click', e);
};

const onConfirm = (e: any): void =>
{
    if (props.to)
        navigateTo(props.to);
    else
        triggerClick(e);
};

const onClick = (e: any): void =>
{
    if (props.confirm.length === 0)
    {
        onConfirm(e);

        if (dropdownButton.value)
            hideDropdown?.();
    }
};
</script>

<template>
    <IdeoButton
        v-bind="buttonAttrs"
        ref="el"
        :type="buttonType"
        :form="submit ? actionBarFormId : undefined"
        :tag="tag"
        :variant="dropdownButton ? 'none' : resolvedVariant"
        :display="buttonDisplay"
        :square="iconOnly"
        :title="title || (backButton ? text : null)"
        :loading="loading"
        :disabled="disabled || loading"
        @click.stop="onClick"
    >
        <IdeoIcon
            v-if="iconName"
            :name="iconName"
            :class="[iconClasses, { 'hidden sm:inline': buttonType === 'submit' && !dropdown }]"
            :size="floatingButton ? 24 : 16"
        />
        <span v-if="textVisible">{{ buttonText }}</span>

        <Confirmation
            v-if="loaded && confirm.length > 0"
            v-bind="confirmAttrs"
            :message="confirm"
            :target="buttonElement"
            placement="auto"
            :value="{}"
            @confirm="onConfirm"
        />
    </IdeoButton>
</template>
